if (!customElements.get("aa-card-carousel")) {
  class AACardCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.track = this.querySelector(".aa-card-carousel__track");
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.prevBtn = this.querySelector(".aa-card-carousel__arrow--prev");
      this.nextBtn = this.querySelector(".aa-card-carousel__arrow--next");
      this.bullets = Array.from(
        this.querySelectorAll(".aa-card-carousel__bullet")
      );
      this.total = this.slides.length;
      this.currentIndex = 0;

      // Variables for drag/swipe detection
      this.isDragging = false;
      this.isMouseDrag = false;
      this.startX = 0;
      this.mouseStartX = 0;
      this.scrollLeftStart = 0;
      this.wasDragging = false;

      // Bind arrow events
      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goTo(this.currentIndex - 1);
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goTo(this.currentIndex + 1);
        });
      }

      // Bind bullet events
      this.bullets.forEach((btn, index) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goTo(index);
        });
      });

      // --- Touch Swipe Logic ---
      this.track.addEventListener(
        "touchstart",
        this.handleTouchStart.bind(this),
        { passive: true }
      );
      this.track.addEventListener(
        "touchmove",
        this.handleTouchMove.bind(this),
        { passive: true }
      );
      this.track.addEventListener("touchend", this.handleTouchEnd.bind(this));

      // --- Mouse Drag Logic (Desktop Swipe) ---
      this.track.addEventListener("mousedown", this.handleMouseDown.bind(this));
      this.track.addEventListener("mousemove", this.handleMouseMove.bind(this));
      this.track.addEventListener("mouseup", this.handleMouseUp.bind(this));
      this.track.addEventListener(
        "mouseleave",
        this.handleMouseLeave.bind(this)
      );

      // Click blocking
      this.track.addEventListener("click", this.handleClick.bind(this));

      // Scroll listener for UI updates
      this.track.addEventListener("scroll", this.onScroll.bind(this), {
        passive: true,
      });

      this.updateUI();

      // Intersection Observer
      this.observer = new IntersectionObserver(
        (entries) => {
          if (this.isAnimating) return;

          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (visible) {
            const idx = this.slides.indexOf(visible.target);
            if (idx !== -1 && idx !== this.currentIndex) {
              this.currentIndex = idx;
              this.updateUI();
            }
          }
        },
        { root: this.track, threshold: 0.5 }
      );

      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    // Touch Handlers
    handleTouchStart(e) {
      this.isDragging = true;
      this.wasDragging = false;
      this.startX = e.touches[0].clientX;
      this.scrollLeftStart = this.track.scrollLeft;
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;
      const x = e.touches[0].clientX;
      const walk = x - this.startX;
      if (Math.abs(walk) > 5) {
        this.wasDragging = true;
      }
    }

    handleTouchEnd(e) {
      this.isDragging = false;
    }

    // Mouse Handlers
    handleMouseDown(e) {
      e.preventDefault(); // Prevent default drag behavior (images etc)
      this.isMouseDrag = true;
      this.wasDragging = false;
      this.mouseStartX = e.pageX - this.track.offsetLeft;
      this.scrollLeftStart = this.track.scrollLeft;
      this.track.style.cursor = "grabbing";
    }

    handleMouseMove(e) {
      if (!this.isMouseDrag) return;
      e.preventDefault();
      const x = e.pageX - this.track.offsetLeft;
      const walk = (x - this.mouseStartX) * 3; // Scroll multiplier (increased sensitivity)
      this.track.scrollLeft = this.scrollLeftStart - walk;

      if (Math.abs(walk) > 5) {
        this.wasDragging = true;
      }
    }

    handleMouseUp(e) {
      this.isMouseDrag = false;
      this.track.style.cursor = "grab";
    }

    handleMouseLeave(e) {
      this.isMouseDrag = false;
      this.track.style.cursor = "grab";
    }

    handleClick(e) {
      if (this.wasDragging) {
        e.preventDefault();
        e.stopPropagation();
        this.wasDragging = false;
        return false;
      }
    }

    onScroll() {
      // Handled by IO
    }

    goTo(index) {
      const targetIndex = Math.max(0, Math.min(index, this.total - 1));

      if (targetIndex === this.currentIndex && index !== this.currentIndex) {
        return;
      }

      this.currentIndex = targetIndex;
      this.isAnimating = true;

      const slide = this.slides[this.currentIndex];
      if (slide) {
        this.track.scrollTo({
          left: slide.offsetLeft,
          behavior: "smooth",
        });
      }

      this.updateUI();

      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    }

    updateUI() {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
      if (this.nextBtn)
        this.nextBtn.disabled = this.currentIndex === this.total - 1;

      this.bullets.forEach((btn, i) => {
        if (i === this.currentIndex) {
          btn.classList.add("active");
          btn.setAttribute("aria-current", "true");
        } else {
          btn.classList.remove("active");
          btn.removeAttribute("aria-current");
        }
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define("aa-card-carousel", AACardCarousel);
}
